import React, { useContext, useEffect, useState, useCallback } from "react"
import { Layout, Menu, Avatar, Button, Tooltip } from "antd"
import menuItems, { MenuItem } from "../../data/menuItems"
import { useTranslation } from "react-i18next"
import { useHistory, useLocation } from "react-router-dom"
import { TitleContext } from "../../contexts/TitleContext"
import { AuthContext } from "../../contexts/auth/AuthContext"
import hasPermission from "../../utils/hasPermission"
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint"
import DraggableMenuButton from "../../components/button/DraggableMenuButton"
import MobileToolBar from "../../components/navigation/MobileToolBar"
import useSettings from "../../hooks/useSettings"
import BrandLogo from "../../components/brand/BrandLogo"
import APP_INFO from "../../constants/appInfo"
import robotAvatar from "../../assets/robot-avatar.svg"
import { Sliders, User } from "react-feather"
import MenuReorderModal, { getCustomMenuItems } from "../../components/modal/MenuReorderModal"
import UserProfileModal from "../../components/modal/UserProfileModal"
import { GET_PROFILE_PHOTO } from "../../rest-api/queries/user"
import { PERMISSIONS_UPDATED_EVENT } from "../../rest-api/queries/me"

export default function Sidebar() {
  const screens = useBreakpoint()
  const [collapsed, setCollapsed] = useState(false)
  const [reorderOpen, setReorderOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const [activeItems, setActiveItems] = useState<MenuItem[]>(getCustomMenuItems())
  const [profilePhoto, setProfilePhoto] = useState<string>(
    localStorage.getItem("user_profile_photo") || ""
  )
  // Ruxsatlar backenddan kelgach menyuni qayta chizish uchun
  const [permissionsVersion, setPermissionsVersion] = useState(0)

  useEffect(() => {
    const onPermissionsUpdated = () => setPermissionsVersion((v) => v + 1)
    window.addEventListener(PERMISSIONS_UPDATED_EVENT, onPermissionsUpdated)
    return () =>
      window.removeEventListener(PERMISSIONS_UPDATED_EVENT, onPermissionsUpdated)
  }, [])

  useEffect(() => {
    const handleUpdate = () => {
      setProfilePhoto(localStorage.getItem("user_profile_photo") || "")
    }
    window.addEventListener("profile_photo_updated", handleUpdate)

    GET_PROFILE_PHOTO().then((res: any) => {
      const url = res?.PhotoUrl || res?.photoUrl
      const fileName = res?.FileName || res?.fileName
      if (url) {
        setProfilePhoto(url)
        localStorage.setItem("user_profile_photo", url)
        if (fileName) localStorage.setItem("user_profile_photo_filename", fileName)
      }
    }).catch(() => {})

    return () => window.removeEventListener("profile_photo_updated", handleUpdate)
  }, [])

  const history = useHistory()
  const { pathname } = useLocation()
  const { setTitle } = useContext(TitleContext)
  const { authState } = useContext(AuthContext)
  const [settings] = useSettings()
  const isClient = authState.user?.userName === "Counterparty"
  const { t } = useTranslation(isClient ? "client" : "translation")

  const refreshMenu = useCallback(() => {
    setActiveItems(getCustomMenuItems())
  }, [])

  function handleMenuItemClick(menuItem: MenuItem) {
    setTitle(menuItem.seoTitle)
    if (!screens.lg) {
      setCollapsed(true)
    }
    history.push(menuItem.link || menuItem.route)
  }

  useEffect(() => {
    // Avval aniq moslik qidiriladi. Ilgari uchala shart bitta find ichida
    // edi va startsWith bo'lak chegarasini tekshirmasdi: "/moneyReceipts"
    // "/money" bilan boshlangani uchun ro'yxatda oldinroq turgan "Ofis Pul"
    // bo'limi topilardi va sahifa sarlavhasi "Офис Деньги" bo'lib chiqardi.
    const exact = menuItems.find(
      (x) => pathname === x.link || pathname === x.route
    )
    // Ichki yo'llar uchun ("/reports/summary") — faqat to'liq bo'lak bo'yicha,
    // "/reports" kabi parametrsiz ko'rinishi ham shu yerga tushadi.
    const byPrefix = menuItems.find(
      (x) =>
        pathname === "/" + x.title ||
        pathname.startsWith("/" + x.title + "/")
    )
    setTitle((exact || byPrefix)?.seoTitle || "")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, setTitle])

  const username = authState.user?.userName || "Admin"

  return (
    <>
      <Layout.Sider
        className={`custom-sider ${settings.toolbar ? "has-toolbar" : ""} ${
          !screens.lg ? "custom-sider-mobile" : ""
        } ${collapsed ? "custom-collapsed" : ""}`}
        collapsible
        collapsed={collapsed}
        onCollapse={(val) => setCollapsed(val)}
        {...(!screens.lg && { trigger: null })}
        collapsedWidth={!screens.lg ? 0 : 80}
        width={265}
      >
        <div className="sidebar-brand-container">
          <BrandLogo collapsed={collapsed} />
        </div>

        {/* User Profile Card */}
        {!collapsed && (
          <div className="sidebar-profile-card">
            <Tooltip placement="right" title={t("tipProfileSettings")}>
              <div
                className="profile-avatar-wrapper"
                onClick={() => setProfileOpen(true)}
                style={{ cursor: "pointer" }}
              >
                <Avatar
                  size={58}
                  src={profilePhoto || robotAvatar}
                  className="profile-avatar-img"
                  style={{
                    border: "2px solid rgba(255,255,255,0.2)",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  }}
                >
                  {username.charAt(0).toUpperCase()}
                </Avatar>
                <span className="profile-online-badge"></span>
              </div>
            </Tooltip>

            <div className="profile-info-wrapper">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span
                  className="profile-name"
                  onClick={() => setProfileOpen(true)}
                  style={{ cursor: "pointer" }}
                >
                  {username}
                </span>
                <Tooltip placement="right" title={t("tipReorderMenu")}>
                  <Button
                    type="text"
                    size="small"
                    icon={<Sliders size={15} color="#3B82F6" />}
                    onClick={() => setReorderOpen(true)}
                    style={{ padding: "2px 4px", height: "auto" }}
                  />
                </Tooltip>
              </div>
              <div className="profile-status-row" onClick={() => setProfileOpen(true)} style={{ cursor: "pointer" }}>
                <span className="status-dot"></span>
                <span className="status-text">Online</span>
              </div>
            </div>
          </div>
        )}

        <Menu
          className="MenuSaidbar_r"
          inlineIndent={0}
          key={`${pathname}-${permissionsVersion}`}
          selectedKeys={["/" + pathname.split("/")[1]]}
          mode="inline"
        >
          {activeItems
            .filter((x) => hasPermission(authState.user, x.route))
            .map((menuItem) => (
              <Menu.Item
                key={menuItem.link || menuItem.route}
                disabled={!menuItem.component}
                onClick={() => handleMenuItemClick(menuItem)}
                icon={<menuItem.icon size={18} />}
              >
                {t(menuItem.title)}
              </Menu.Item>
            ))}
        </Menu>

        {!collapsed && (
          <div className="sidebar-footer-info">
            <div style={{ marginBottom: 6 }}>
              <Button
                type="dashed"
                size="small"
                block
                icon={<Sliders size={13} style={{ marginRight: 6 }} />}
                onClick={() => setReorderOpen(true)}
                style={{
                  fontSize: "12px",
                  borderRadius: "6px",
                  borderColor: "rgba(100, 116, 139, 0.3)",
                  color: "#64748B",
                }}
              >
                Menyuni sozlash
              </Button>
            </div>
            <span className="footer-title">{APP_INFO.name}</span>
            <span className="footer-copyright">© 2026 All Rights Reserved</span>
          </div>
        )}
      </Layout.Sider>

      <MenuReorderModal
        visible={reorderOpen}
        onClose={() => setReorderOpen(false)}
        onOrderChanged={refreshMenu}
      />

      <UserProfileModal
        visible={profileOpen}
        onClose={() => setProfileOpen(false)}
      />

      {!screens.lg &&
        (settings.toolbar ? (
          <MobileToolBar onMenuButtonClicked={() => setCollapsed(!collapsed)} />
        ) : (
          <DraggableMenuButton onClick={() => setCollapsed(!collapsed)} />
        ))}
    </>
  )
}
